import { closeDrawer, openDrawer } from "@/src/components/dialogs";
import {
  FolderItem,
  Intake,
  Medicine,
  MedicineHoraire,
  OrdonnanceCategory,
  OrdonnanceMedicine,
  OrdonnanceTemplate,
  Patient,
  dilutionSolutions,
  medicineHoraires,
  medicineRoutes,
  medicineUnits,
  ordonnanceDurations,
  taskDurations,
} from "@/src/data/allTypes";
import apiFetch from "@/src/utils/apiFetch";
import {
  getOptionsFromRecord,
  getUserId,
  getUserShortName,
} from "@/src/utils/utils";

import { Button, CornerIconButton } from "@/src/components/buttons";
import { Card } from "@/src/components/cards";
import {
  FetchableList,
  ListConstructor,
} from "@/src/components/fetchableLists";
import SearchKeyHighliter from "@/src/components/seachkeyHighlighter";
import { Input } from "@/src/components/universalInput";
import { USER } from "@/src/data/globalVariables";
import { formatDate, getItemId, jsonAbstract, uid } from "@/src/utils/utils";
import { useQuery } from "@tanstack/react-query";
import moment from "moment";
import { useState } from "react";
import { IoAdd } from "react-icons/io5";

export function MedicinesChoiceList({
  onSelect,
}: {
  onSelect: (newValue: Medicine) => void;
}) {
  const [reload, setReload] = useState(0);
  let [searchKey, setSearchKey] = useState("");

  return (
    <div>
      <FetchableList
        queryKey={["MedicinesChoiceList", searchKey, reload]}
        queryFn={() =>
          apiFetch(
            "medicines",
            "loadMany",
            { searchKey },
            "Erreur lors du chargement des médicaments"
          )
        }
        searchKey={searchKey}
        onSearch={(newValue) => {
          setSearchKey(newValue || "");
        }}
        emptyChild={
          searchKey && (
            <div className="center grid c1 gap10 p20">
              Aucun médicament disponible
              <Button
                onClick={async () => {
                  let medicine: Medicine = {
                    name: `${searchKey || ""}`,
                    defaultRoute: "orale",
                    defaultUnit: "comprimé",
                    form: "comprimé",
                    creator: getUserId(),
                  };

                  await openDrawer({
                    title: "Ajouter un médicament",
                    content: (
                      <MedicineForm
                        current={medicine}
                        onChange={async (newValue) => {
                          medicine = newValue;
                        }}
                      />
                    ),
                    onConfirm: async () => {
                      await apiFetch(
                        "medicines",
                        "addOne",
                        { medicine },
                        "Erreur lors de l'ajout"
                      );
                      setSearchKey("");
                      setReload((prev) => prev + 1);
                    },
                  });
                }}
              >
                Créer {searchKey}
              </Button>
            </div>
          )
        }
      >
        {(data) => (
          <div className="grid c1 gap10">
            {data?.map((medicine, index) => {
              if (!medicine) return null;
              return (
                <Card
                  key={uid()}
                  color="purple"
                  trailing={
                    <div className="gray">{medicine.usageCount || ""}</div>
                  }
                  onClick={async () => {
                    onSelect(medicine);
                  }}
                >
                  <div className="capitalize">
                    <SearchKeyHighliter searchKey={searchKey}>
                      {medicine.name}
                    </SearchKeyHighliter>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </FetchableList>
    </div>
  );
}

export function MedicineForm({
  current,
  onChange,
}: {
  current?: Medicine;
  onChange: (newValue: Medicine) => void;
}) {
  let [data, setData] = useState(current || ({} as Medicine));
  function setNewValue(newValue: Medicine) {
    setData(newValue);
    onChange(newValue);
  }

  return (
    <div className="grid c1 gap10">
      <Input
        name="Nom commercial"
        value={data.name}
        type="text"
        placeholder="Ex: Doliprane"
        onChange={(newValue) => {
          setNewValue({ ...data, name: newValue });
        }}
      />
      <Input
        name="DCI"
        value={data.dci}
        type="text"
        placeholder="Ex: Paracetamol"
        onChange={(newValue) => {
          setNewValue({ ...data, dci: newValue });
        }}
      />
    </div>
  );
}

export function MedicinesAndTemplatesChoiceList({
  showRenewButton,
  patient,
  parent,
  initialTemplates,
  initialMedicines,
  onSelect,
}: {
  showRenewButton: boolean;
  patient: Patient;
  parent?: FolderItem | null | undefined;
  initialTemplates?: (OrdonnanceTemplate | null | undefined)[];
  initialMedicines?: (Medicine | null | undefined)[];
  onSelect: (
    medicine: Medicine | null,
    template: OrdonnanceTemplate | null
  ) => void;
}) {
  const [reload, setReload] = useState(0);
  initialTemplates ||= USER.get()?.speciality?.frequentOrdonnanceTemplates;
  initialMedicines ||= USER.get()?.speciality?.frequentMedicines;
  let [searchKey, setSearchKey] = useState("");
  let { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["MedicinesAndTemplatesChoiceList", searchKey],
    queryFn: () =>
      apiFetch(
        "medicines",
        "loadMedicinesAndTemplates",
        {
          searchKey,
          initialTemplateIds: (initialTemplates?.map((item) =>
            getItemId(item)
          ) || []) as string[],
          // initialMedicinesIds: initialMedicines?.map((item) =>
          //   getItemId(item)
          // ) as string[],
        },
        "Erreur lors du chargement des médicaments"
      ),
  });

  let medicines = data?.medicines || [];
  let templates: (OrdonnanceTemplate | null | undefined)[] =
    data?.templates || [];

  return (
    <div>
      <ListConstructor
        data={[...(data?.medicines || []), ...(data?.templates || [])]}
        isLoading={isLoading}
        isError={isError}
        error={error}
        searchKey={searchKey}
        onSearch={(newValue) => {
          setSearchKey(newValue || "");
        }}
        emptyChild={
          searchKey && (
            <div className="center grid c1 gap10 p20">
              Aucun médicament disponible
              <Button
                onClick={async () => {
                  let medicine: Medicine = {
                    name: `${searchKey || ""}`,
                    defaultRoute: "orale",
                    defaultUnit: "comprimé",
                    form: "comprimé",
                    creator: getUserId(),
                  };

                  await openDrawer({
                    title: "Ajouter un médicament",
                    additionalClasses: ["big"],
                    content: (
                      <MedicineForm
                        current={medicine}
                        onChange={async (newValue) => {
                          medicine = newValue;
                        }}
                      />
                    ),
                    onConfirm: async () => {
                      await apiFetch(
                        "medicines",
                        "addOne",
                        { medicine },
                        "Erreur lors de l'ajout"
                      );
                      setSearchKey("");
                      setReload((prev) => prev + 1);
                    },
                  });
                }}
              >
                Créer {searchKey}
              </Button>
            </div>
          )
        }
      >
        <div className="grid c1 gap10">
          {showRenewButton == true && !searchKey && (
            <RenewOrdonnanceButton
              patient={patient}
              parent={parent}
              onSelect={onSelect}
            ></RenewOrdonnanceButton>
          )}
          {templates.map((template, index) => {
            if (!template) return null;
            return (
              <Card
                key={uid()}
                color="simple"
                onClick={async () => {
                  onSelect(null, template);
                }}
                title={
                  <SearchKeyHighliter
                    className={"capitalize"}
                    searchKey={searchKey}
                  >
                    {template.name}
                  </SearchKeyHighliter>
                }
              >
                <div>
                  <div className="gray">
                    Modèle de{" "}
                    <strong>{template.ordonnanceMedicines?.length}</strong>{" "}
                    Médicaments
                  </div>
                </div>
              </Card>
            );
          })}
          {medicines.map((medicine, index) => {
            return (
              <Card
                key={uid()}
                color="simple"
                trailing={
                  <div className="gray">{medicine.usageCount || ""}</div>
                }
                title={
                  <div className={"capitalize"}>
                    <SearchKeyHighliter searchKey={searchKey}>
                      {`${medicine.name}`}
                    </SearchKeyHighliter>
                    <span className="normal"> {medicine.dosage}</span>
                  </div>
                }
                subTitle={medicine.dci}
                onClick={async () => {
                  onSelect(medicine, null);
                }}
              ></Card>
            );
          })}
        </div>
      </ListConstructor>
    </div>
  );
}

function RenewOrdonnanceButton({
  patient,
  parent,
  onSelect,
}: {
  patient: Patient;
  parent?: FolderItem | null | undefined;
  onSelect: (
    medicine: Medicine | null,
    template: OrdonnanceTemplate | null
  ) => void;
}) {
  let { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["RenewOrdonnanceButton", patient.id, parent?.id],
    queryFn: () =>
      apiFetch(
        "ordonnances",
        "loadMany",
        {
          categories: null,
          patientId: patient.id,
          parentId: null,
        },
        "Erreur lors du chargement des ordonnances"
      ),
  });

  let ambulatoryOrdonnances =
    data?.filter((ordonnance) => ordonnance.category == "ambulatory") || [];
  let hospitalOrdonnances =
    data?.filter(
      (ordonnance) =>
        ordonnance.category == "hospital" &&
        getItemId(ordonnance.parent) == getItemId(parent)
    ) || [];

  async function getOrdonnanceFromTasks() {
    let tasks = await apiFetch("tasks", "loadMany", {
      parentId: getItemId(parent),
    });
    let todayTasks = tasks.filter((task) => {
      return moment(task.start).isSame(moment(), "day");
    });

    let ordonnanceMedicines: OrdonnanceMedicine[] = [];
    for (let task of todayTasks) {
      if (!task.medicine) continue;
      if (
        ordonnanceMedicines.find(
          (item) => getItemId(item.medicine) == getItemId(task.medicine)
        )
      )
        continue;

      let ordonnanceMedicine: OrdonnanceMedicine = {
        medicine: task.medicine,
        patient: getItemId(patient),
        startDate: moment().format("YYYY-MM-DD"),
        intakes: [],
        tasks: [],
        notes: "",
        creator: getUserId(),
        
      };
      ordonnanceMedicines.push(ordonnanceMedicine);
    }

    for (let task of todayTasks) {
      let ordonnanceMedicine = ordonnanceMedicines.find(
        (item) => getItemId(item.medicine) == getItemId(task.medicine)
      );
      if (!ordonnanceMedicine) continue;

      let taskHoraire = moment(task.start).format(
        "HH:00:00"
      ) as MedicineHoraire;
      let horaireInfo = medicineHoraires[taskHoraire];

      let intake: Intake = {
        creator: getUserId(),
        posology: task.posology,
        route: task.route,
        unit: task.unit,
        dilutionVolume: null,
        duration: 0,
        horaire: horaireInfo?.getHoraire?.() || "1x/j",
        solution: null,
        notes: "",
        
      };

      ordonnanceMedicine.intakes.push(intake);
    }

    let template: OrdonnanceTemplate = {
      category: "ambulatory",
      ordonnanceMedicines,
      creator: getUserId(),
      name: "none",
    };

    await onSelect(null, template);
  }

  return (
    <ListConstructor
      data={data}
      isLoading={isLoading}
      isError={isError}
      error={error}
      emptyChild={<div>Aucune ordonnance précédente</div>}
    >
      <Card
        key={uid()}
        color="simple"
        title={<div>Renouveler ordonnance</div>}
        onClick={async () => {
          openDrawer({
            id: "RenewOrdonnanceDrawer",
            title: "Renouveler ordonnance",
            content: (
              <div className="c1 gap10">
                {parent?.type == "hospitalisation" &&
                  hospitalOrdonnances?.length != 0 && (
                    <Card
                      key={uid()}
                      color="simple"
                      title={
                        <div>Utiliser le traitement intra-hospitalier</div>
                      }
                      onClick={async () => {
                        await getOrdonnanceFromTasks();
                        closeDrawer("RenewOrdonnanceDrawer");
                      }}
                    />
                  )}
                {ambulatoryOrdonnances.map((ordonnance, index) => {
                  return (
                    <Card
                      key={uid()}
                      color="simple"
                      title={
                        <div>{formatDate(ordonnance._date, "LL à HH:mm")}</div>
                      }
                      onClick={async () => {
                        let template: OrdonnanceTemplate = {
                          category: "ambulatory",
                          ordonnanceMedicines: ordonnance.ordonnanceMedicines,
                          creator: getUserId(),
                          name: "none",
                        };

                        await onSelect(null, template);
                        closeDrawer("RenewOrdonnanceDrawer");
                      }}
                    >
                      <div className="c1 gap5 mtop5">
                        <div className="capitalize">
                          {ordonnance.ordonnanceMedicines?.map(
                            (ordonnanceMedicine) => {
                              return (
                                <div key={uid()}>
                                  - {ordonnanceMedicine?.medicine?.name}
                                </div>
                              );
                            }
                          )}
                        </div>
                        <div className="gray">
                          {getUserShortName(ordonnance.creator)}
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
            ),
          });
        }}
      ></Card>
    </ListConstructor>
  );
}
